'use client';

import { SessionProvider, useSession, signOut } from 'next-auth/react';
import { useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { invalidateClientSessionCache } from '@/core/api/auth-helper';

// Admin-only (shown when the refresh token dies mid-work), but this provider wraps every page:
// imported statically it put the whole re-login form — inputs, the 2FA step — into every
// storefront visitor's JavaScript. Loaded only when it actually has to be shown.
const SessionExpiredModal = dynamic(() => import('./SessionExpiredModal').then((m) => m.SessionExpiredModal), { ssr: false });

function SessionWatcher() {
    const { data: session, status, update } = useSession();
    const pathname = usePathname() || '';
    const isAdmin = pathname.startsWith('/admin');
    const lastActivityPingRef = useRef<number>(0);

    useEffect(() => {
        lastActivityPingRef.current = Date.now();
    }, []);

    useEffect(() => {
        if (status === 'unauthenticated') {
            invalidateClientSessionCache();
        }
    }, [status]);

    useEffect(() => {
        // NextAuth broadcasts sign-in/sign-out across tabs via a 'storage' event on the
        // 'next-auth.message' key.
        const onStorage = (event: StorageEvent) => {
            if (event.key === 'next-auth.message') {
                invalidateClientSessionCache();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // Sliding session / Activity Heartbeat in admin:
    // When the user actively interacts in the admin panel (mouse, clicks, typing),
    // periodically trigger session refresh (throttled to at most once every 4 minutes)
    // so active merchants never get logged out mid-work.
    const onUserActivity = useCallback(() => {
        if (!isAdmin || status !== 'authenticated') return;
        const now = Date.now();
        const FOUR_MINUTES = 4 * 60 * 1000;
        if (now - lastActivityPingRef.current > FOUR_MINUTES) {
            lastActivityPingRef.current = now;
            update().catch(() => {});
        }
    }, [isAdmin, status, update]);

    useEffect(() => {
        if (!isAdmin || status !== 'authenticated') return;
        const events = ['mousedown', 'keydown', 'touchstart'] as const;
        events.forEach((evt) => window.addEventListener(evt, onUserActivity, { passive: true }));
        return () => {
            events.forEach((evt) => window.removeEventListener(evt, onUserActivity));
        };
    }, [isAdmin, status, onUserActivity]);

    // Handle token refresh failure:
    // 1. If on storefront (!isAdmin): silently clear expired session cookie so user
    //    can browse freely as a guest without any disruptive blocking modals.
    // 2. If in admin: display SessionExpiredModal to save user's in-progress work.
    const hasRefreshError = (session as any)?.error === 'RefreshAccessTokenError';

    useEffect(() => {
        if (!isAdmin && hasRefreshError) {
            invalidateClientSessionCache();
            signOut({ redirect: false });
        }
    }, [isAdmin, hasRefreshError]);

    if (isAdmin && hasRefreshError && session?.user?.email) {
        return <SessionExpiredModal email={session.user.email} />;
    }

    return null;
}

export default function NextAuthProvider({ children, session }: { children: React.ReactNode; session?: any }) {
    return (
        // `session` is the server-rendered session (root layout's getServerSession) —
        // seeding it here means useSession() resolves synchronously on first render
        // instead of starting in its 'loading' state.
        //
        // refetchOnWindowFocus={true} ensures returning to an open tab after a break immediately
        // refreshes the session before any form submission or button click occurs.
        // refetchInterval={240} (4 minutes) keeps tokens actively renewed within the 60min lifespan.
        <SessionProvider session={session} refetchOnWindowFocus={true} refetchInterval={240}>
            <SessionWatcher />
            {children}
        </SessionProvider>
    );
}
